import { useState, useEffect, useCallback } from 'react';
import { FileText, Plus, Edit2, Trash2, X } from 'lucide-react';
import { logger } from '../../utils/logger';

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminTemplates() {
  const [templates, setTemplates] = useState([]);
  const [showEditor, setShowEditor] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', body: '' });

  const fetchTemplates = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/admin/templates`, { credentials: 'include' });
      if (res.ok) setTemplates((await res.json()).templates || []);
    } catch (err) { logger.error("Operation failed:", err); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  const save = async () => {
    const method = editing ? 'PUT' : 'POST';
    const url = editing ? `${API}/api/admin/templates/${editing}` : `${API}/api/admin/templates`;
    await fetch(url, { method, credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(form) });
    setShowEditor(false); setEditing(null); setForm({ title: '', body: '' }); fetchTemplates();
  };

  const del = async (id) => {
    if (!window.confirm('Delete?')) return;
    await fetch(`${API}/api/admin/templates/${id}`, { method: 'DELETE', credentials: 'include' }); fetchTemplates();
  };

  return (
    <div data-testid="admin-templates" className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Quick Reply Templates</h1>
        <button onClick={() => { setEditing(null); setForm({ title: '', body: '' }); setShowEditor(true); }}
          className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-violet-600 to-cyan-500 text-white rounded-lg text-sm font-medium">
          <Plus className="w-4 h-4" /> New Template
        </button>
      </div>

      {showEditor && (
        <div className="bg-[#0E1830] border border-white/10 rounded-xl p-6 space-y-4">
          <div className="flex items-center justify-between"><h2 className="text-white font-semibold">{editing ? 'Edit' : 'Create'} Template</h2>
            <button onClick={() => setShowEditor(false)} className="text-white/40 hover:text-white"><X className="w-4 h-4" /></button>
          </div>
          <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Template title"
            className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-sm text-white focus:outline-none" />
          <textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} placeholder="Template body..."
            rows={5} className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-sm text-white resize-none focus:outline-none" />
          <button onClick={save} disabled={!form.title || !form.body}
            className="px-6 py-2 bg-cyan-600 text-white rounded-lg text-sm disabled:opacity-30">Save</button>
        </div>
      )}

      <div className="space-y-2">
        {templates.map(t => (
          <div key={t.id} className="bg-[#0E1830] border border-white/10 rounded-xl p-4 flex items-center justify-between">
            <div>
              <div className="text-white font-medium text-sm">{t.title}</div>
              <div className="text-white/40 text-xs mt-1 line-clamp-1">{t.body}</div>
              <div className="text-white/20 text-xs mt-1">Used {t.use_count || 0} times</div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => { setEditing(t.id); setForm({ title: t.title, body: t.body }); setShowEditor(true); }} className="p-1.5 text-white/30 hover:text-white"><Edit2 className="w-4 h-4" /></button>
              <button onClick={() => del(t.id)} className="p-1.5 text-white/30 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
            </div>
          </div>
        ))}
        {templates.length === 0 && <p className="text-white/40 text-center py-8">No templates yet</p>}
      </div>
    </div>
  );
}
